import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { AlertCircle, Plus, Trash2, XCircle } from 'lucide-react';
import { Alert, AlertDescription } from '../../ui/alert';
import { toast } from 'sonner';
import { useCreateDataEQConnector } from '../../../hooks/useDataEQConnector';
import { handleMutationError } from '../../../utils/errorHandling';
import { CONNECTOR_CONFIGS } from '../../../constants/connectors';

interface AddDataEQConnectorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConnectorCreated?: (connectorData: any) => void;
  preselectedType?: string;
}

// Account with frontend ID for stable React keys
interface AccountWithId {
  id: string; // Frontend-only, for React key stability
  code: string;
  dataeqApiKey: string;
}

// DataEQ connector configuration with styling
const DATAEQ_CONFIG = {
  ...CONNECTOR_CONFIGS.dataeq,
  color: 'bg-purple-100 text-purple-700 border-purple-200'
};

const AddDataEQConnectorModal: React.FC<AddDataEQConnectorModalProps> = ({
  open,
  onOpenChange,
  onConnectorCreated,
  preselectedType,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    dataeqAccounts: [{ id: crypto.randomUUID(), code: '', dataeqApiKey: '' }] as AccountWithId[],
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [generalError, setGeneralError] = useState<string>('');
  const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Use the create connector mutation
  const createConnector = useCreateDataEQConnector();

  // Auto-populate form when modal opens
  useEffect(() => {
    if (open) {
      setFormData(prev => ({
        ...prev,
        name: `${DATAEQ_CONFIG.name} Connector`,
      }));

      // Clear any text selection after modal opens and form is populated
      setTimeout(() => {
        const modal = document.querySelector('[role="dialog"]');
        if (modal) {
          const inputs = modal.querySelectorAll('input');
          inputs.forEach((input: HTMLInputElement) => {
            if (input.value && input.selectionStart !== null) {
              input.setSelectionRange(input.value.length, input.value.length);
            }
          });
        }
      }, 50);
    }

    // Clear any existing timeout when modal opens/closes
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }
  }, [preselectedType, open]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, []);

  const resetForm = () => {
    setFormData({
      name: '',
      dataeqAccounts: [{ id: crypto.randomUUID(), code: '', dataeqApiKey: '' }],
    });
    setErrors({});
    setGeneralError('');
  };

  const handleModalClose = (isOpen: boolean) => {
    if (!isOpen) {
      resetForm();
    }
    onOpenChange(isOpen);
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Connector name is required';
    }

    // DataEQ-specific validation: all accounts must have both code and API key
    const hasEmptyAccount = formData.dataeqAccounts.some(
      account => !account.code.trim() || !account.dataeqApiKey.trim()
    );

    if (hasEmptyAccount) {
      newErrors.dataeqAccounts = 'All account codes and API keys are required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    // Strip frontend-only 'id' field before sending to backend
    const accountsToSend = formData.dataeqAccounts.map(({ code, dataeqApiKey }) => ({
      code,
      dataeqApiKey,
    }));

    createConnector.mutate(
      {
        connectorName: formData.name,
        dataeqAccounts: accountsToSend,
      },
      {
        onSuccess: (response) => {
          // Close modal
          onOpenChange(false);

          // Notify parent component
          if (onConnectorCreated) {
            onConnectorCreated(response);
          }

          // Show success toast
          toast.success('Connector Created', {
            description: `${response.organisation?.name || formData.name} connector has been created and configured with ${response.connector.dataeqAccounts.length} account(s).`,
          });

          // Reset form
          resetForm();
        },
        onError: (error) => {
          const errorInfo = handleMutationError(error, setErrors, {
            conflictTitle: 'Connector Already Exists',
            conflictDescription: 'A DataEQ connector has already been configured for this organization.',
            genericTitle: 'Failed to Create Connector',
            genericDescription: 'An error occurred while creating the connector. Please try again later.',
          });

          // Show error message in the in-form alert for ALL error types
          setGeneralError(errorInfo.description);

          // Clear any existing timeout
          if (errorTimeoutRef.current) {
            clearTimeout(errorTimeoutRef.current);
          }

          // Set timeout to clear error after 8 seconds
          errorTimeoutRef.current = setTimeout(() => {
            setGeneralError('');
            errorTimeoutRef.current = null;
          }, 8000);
        },
      }
    );
  };

  const addDataEQAccount = () => {
    setFormData(prev => ({
      ...prev,
      dataeqAccounts: [...prev.dataeqAccounts, { id: crypto.randomUUID(), code: '', dataeqApiKey: '' }]
    }));
  };

  const removeDataEQAccount = (index: number) => {
    setFormData(prev => ({
      ...prev,
      dataeqAccounts: prev.dataeqAccounts.filter((_, i) => i !== index)
    }));
  };

  const updateDataEQAccount = (index: number, field: 'code' | 'dataeqApiKey', value: string) => {
    setFormData(prev => ({
      ...prev,
      dataeqAccounts: prev.dataeqAccounts.map((account, i) =>
        i === index ? { ...account, [field]: value } : account
      )
    }));
  };

  return (
    <Dialog open={open} onOpenChange={handleModalClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Configure {DATAEQ_CONFIG.name} Connector</DialogTitle>
          <DialogDescription>
            Set up your {DATAEQ_CONFIG.name} integration for automated data ingestion
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Show DataEQ connector info at the top */}
          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
            <div className="p-2 rounded-lg bg-white">
              {DATAEQ_CONFIG.icon}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{DATAEQ_CONFIG.name}</h3>
              <p className="text-sm text-gray-600">{DATAEQ_CONFIG.description}</p>
            </div>
          </div>

          {/* General Error Alert */}
          {generalError && (
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertDescription>{generalError}</AlertDescription>
            </Alert>
          )}

          {/* Configuration Form */}
          <div className="space-y-4">
            {/* Connector Name */}
            <div className="space-y-2 mb-4">
              <Label htmlFor="name">Connector Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder={`e.g., ${DATAEQ_CONFIG.name} Production`}
                autoFocus={false}
                onFocus={(e) => {
                  e.target.setSelectionRange(e.target.value.length, e.target.value.length);
                }}
              />
              {errors.name && (
                <p className="text-sm text-red-500">{errors.name}</p>
              )}
            </div>

            {/* DataEQ Accounts */}
            <div className="space-y-4">
              <Label>DataEQ Accounts</Label>
              <div className="space-y-3">
                {formData.dataeqAccounts.map((account, index) => (
                  <div key={account.id} className="border rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-medium">Account {index + 1}</h4>
                      {formData.dataeqAccounts.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeDataEQAccount(index)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor={`account-code-${index}`}>Code</Label>
                        <Input
                          id={`account-code-${index}`}
                          value={account.code}
                          onChange={(e) => updateDataEQAccount(index, 'code', e.target.value)}
                          placeholder="e.g., ADM001"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor={`api-key-${index}`}>DataEQ API Key</Label>
                        <Input
                          id={`api-key-${index}`}
                          type="password"
                          value={account.dataeqApiKey}
                          onChange={(e) => updateDataEQAccount(index, 'dataeqApiKey', e.target.value)}
                          placeholder="Enter API key"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addDataEQAccount}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Account
              </Button>
              {errors.dataeqAccounts && (
                <p className="text-sm text-red-500">{errors.dataeqAccounts}</p>
              )}
            </div>

            {/* Next Steps Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-blue-700">
                  You can still add and remove accounts after creation
                </p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={createConnector.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={createConnector.isPending}
          >
            {createConnector.isPending ? 'Creating...' : 'Create Connector'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddDataEQConnectorModal;